// tslint:disable: no-inferrable-types
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';

import { BackendObjectRef } from '../lib/backend-integration.service';
import { State } from '../lib/state.model';
import { Transition } from '../lib/transition.model';
import { SelectControlGroup } from '../lib/workflow-editor.store';

export interface WorkflowInitialTransitionFormOptions {
  transition: Transition|undefined;
  states: State[];
  roles: SelectControlGroup<BackendObjectRef>[];
}

interface WorkflowInitialTransitionFormValue {
  name: string;
  roles: string[];
  target: State|undefined;
  targetNew?: string;
}

interface WorkflowInitialTransitionFormResult {
  name: string;
  roles: string[];
  target: State;
}

export class WorkflowInitialTransitionForm {

  private static NewStateOption: State = new State('-1', 'ADD NEW STATE...');

  private static FORM_DEFAULT_VALUE: WorkflowInitialTransitionFormValue = {
    name: '', roles: [], target: undefined
  };

  public isAddingTarget: boolean = false;
  public isRenamingTarget: boolean = false;

  public get canRenameTarget(): boolean {
    return !this.isAddingTarget && !this.isRenamingTarget;
  }

  public readonly form: FormGroup;

  public readonly roles: SelectControlGroup<BackendObjectRef>[];

  public readonly states: State[];

  public readonly transition?: Transition;

  public get value(): WorkflowInitialTransitionFormResult {
    const result: {[K: string]: any} = {
      name: this.form.value.name,
      roles: this.form.value.roles,
    };
    if (this.isAddingTarget) {
      result.target = new State(undefined, this.form.value.targetNew);
    } else if (this.isRenamingTarget) {
      result.target = new State(this.form.value.target.id,
                                this.form.value.targetNew);
    } else {
      result.target = this.form.value.target;
    }

    return result as WorkflowInitialTransitionFormResult;
  }

  public get dirty(): boolean {
    return this.form.dirty;
  }

  public static isNewStateOption(ref: BackendObjectRef|undefined): boolean {
    if (!ref) { return false; }
    return ref.id === this.NewStateOption.id;
  }

  private static buildFormGroup(formBuilder: FormBuilder,
                                transition?: Transition): FormGroup {
    const value = transition
                  ? { ...this.FORM_DEFAULT_VALUE, ...transition }
                  : this.FORM_DEFAULT_VALUE;

    const controls: {[P in keyof WorkflowInitialTransitionFormValue]: any} = {
      name:             [value.name, Validators.required],
      roles:            [value.roles, Validators.required],
      target:           [value.target, Validators.required]
    };

    const options = {};
    return formBuilder.group(controls, options);
  }

  constructor(formBuilder: FormBuilder,
              transition: Transition|undefined,
              states: State[],
              roles: SelectControlGroup<BackendObjectRef>[]) {
      this.form =
        WorkflowInitialTransitionForm.buildFormGroup(formBuilder, transition);

      this.roles = roles;
      this.states = [WorkflowInitialTransitionForm.NewStateOption, ...states];
      this.transition = transition;

      const targetControl = this.form.get('target') as FormControl;
      targetControl.valueChanges.subscribe(value => {
        if (this.isRenamingTarget) { this.cancelRenameTargetState(); }

       WorkflowInitialTransitionForm.isNewStateOption(value)
         ? this.startAddingTarget()
         : this.cancelAddingTarget();
      });
  }

  public valid(path?: string | (string | number)[]): boolean {
    if (!path) { return this.form.valid; }
    const control = this.form.get(path);
    return control ? control.valid : false;
  }

  public invalid(path?: string | (string | number)[]): boolean {
    return !this.valid(path);
  }

  public hasError(errorCode: string, path?: (string|number)[] | string): boolean {
    return this.form.hasError(errorCode, path);
  }

  public addTargetNewControl() {
    const control = new FormControl('', Validators.required);
    this.form.addControl('targetNew', control);
  }

  public removeTargetNewControl() {
    this.form.removeControl('targetNew');
  }

  public startAddingTarget() {
    this.addTargetNewControl();
    this.isAddingTarget = true;
  }

  public cancelAddingTarget() {
    this.isAddingTarget = false;
    this.removeTargetNewControl();
  }

  public startRenameTargetState() {
    this.isRenamingTarget = true;
    this.addTargetNewControl();
  }

  public cancelRenameTargetState() {
    this.isRenamingTarget = false;
    this.removeTargetNewControl();
  }
}
